/**
 * Safely parse a 12-hour time string.
 * Accepts:
 *   - "10:33 AM"
 *   - "10 AM"
 *   - "12:00 PM"
 * Returns { hh, mi } in 24h time, or NaN if invalid.
 */
function parseTime12(time12) {
  if (!time12 || typeof time12 !== 'string') {
    return { hh: NaN, mi: NaN };
  }

  const match = time12.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) {
    return { hh: NaN, mi: NaN };
  }

  let hh = Number(match[1]);
  let mi = Number(match[2] ?? 0);
  const ampm = match[3].toUpperCase();

  if (ampm === 'PM' && hh < 12) hh += 12;
  if (ampm === 'AM' && hh === 12) hh = 0;

  return { hh, mi };
}

/**
 * Get formatted parts for a UTC timestamp as seen in a specific IANA timezone.
 */
function partsInTimeZone(ms, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(ms));

  const map = {};
  for (const p of parts) map[p.type] = p.value;

  return {
    y: Number(map.year),
    m: Number(map.month),
    d: Number(map.day),
    h: Number(map.hour),
    mi: Number(map.minute),
    s: Number(map.second),
  };
}

/**
 * Convert a wall-clock time in an IANA timezone to an absolute UTC timestamp.
 */
function zonedWallToUtcMs({ y, m, d, h, mi, s = 0 }, timeZone) {
  // Initial guess: treat the wall time as if it were UTC
  let t = Date.UTC(y, m - 1, d, h, mi, s, 0);

  // Refine until the formatted parts match the intended wall time
  for (let i = 0; i < 3; i++) {
    const got = partsInTimeZone(t, timeZone);

    const wantUtc = Date.UTC(y, m - 1, d, h, mi, s, 0);
    const gotUtc  = Date.UTC(got.y, got.m - 1, got.d, got.h, got.mi, got.s, 0);

    const delta = wantUtc - gotUtc;
    if (delta === 0) break;

    t += delta;
  }

  return t;
}

/**
 * Returns the event start timestamp (ms since epoch),
 * correctly interpreted in the event's IANA timezone.
 */
export function getEventStartMs(event) {
  if (!event?.eventDate || !event?.startTime12) return null;

  const [y, m, d] = String(event.eventDate).split('-').map(Number);
  if (!y || !m || !d) return null;

  const { hh, mi } = parseTime12(event.startTime12);
  if (!Number.isFinite(hh) || !Number.isFinite(mi)) return null;

  const tz = event.timeZone || 'America/Los_Angeles';

  return zonedWallToUtcMs(
    { y, m, d, h: hh, mi, s: 0 },
    tz
  );
}

/**
 * Returns the event end timestamp (ms since epoch),
 * correctly interpreted in the event's IANA timezone.
 */
export function getEventEndMs(event) {
  if (!event?.eventDate || !event?.endTime12) return null;

  const [y, m, d] = String(event.eventDate).split('-').map(Number);
  if (!y || !m || !d) return null;

  const { hh, mi } = parseTime12(event.endTime12);
  if (!Number.isFinite(hh) || !Number.isFinite(mi)) return null;

  const tz = event.timeZone || 'America/Los_Angeles';

  return zonedWallToUtcMs(
    { y, m, d, h: hh, mi, s: 0 },
    tz
  );
}
